import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import AxiosConfig from '../../../../../../config/AxiosConfig';
import UserMentionManager from '../../../../../helpers/utils/UserMentionManager';
import useNote from '../../../../../hooks/project/useNote';
import DownloadableAttachment from '../../../../utils/common/DownloadableAttachment';
import Dropzone from '../../../../utils/common/Dropzone';
import LoadingButton from '../../../../utils/common/LoadingButton';
import TextFieldWithMentions from '../../../../utils/common/TextFieldWithMentions';
import TypographyWithHighlightMentions from '../../../../utils/common/TypographyWithHighlightMentions';

const ManageTask = ({ task, mutateTicket, mutateTask }) => {
  const profile = useSelector((state) => state?.login?.profile);
  const _users = useSelector((state) => state?.static?.users);
  const navigate = useNavigate();
  const { notes, mutate } = useNote(task?._id, true);
  const noteTypes = useSelector((state) => state?.static?.noteTypes);
  const services = useSelector((state) => state?.static?.services) || [];
  const [selectedService, setSelectedService] = useState('');
  const [scheduledDate, setScheduledDate] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [currentNote, setCurrentNote] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: mutateUsers } = useSWRConfig();
  const [isTaskEnded, setIsTaskEnded] = useState(false);
  const [attachmentsSize, setTotalAttachmentsSize] = useState(0);
  const notesContainerRef = useRef(null);
  const userMentionManager = new UserMentionManager(); // Créer l'instance de la classe
  const theme = useTheme();

  useEffect(() => {
    setSelectedService(task?.service?._id);
    setScheduledDate(task?.scheduledAt ? new Date(task?.scheduledAt) : null);
  }, [task]);

  useEffect(() => {
    if (!currentNote) {
      AxiosConfig.create('post', `/notes/`)
        .call({
          project: task?.ticket?.project?._id,
          ticket: task?.ticket?._id,
          task: task?._id,
          type: noteTypes.find((noteType) => noteType?.code === 'TASKNOTE')
            ?._id,
          user: profile?._id,
        })
        .exit((res) => {
          setCurrentNote(res?.data);
        })
        .catch((err) => console.error(err));
    }
  }, [currentNote]);

  useEffect(() => {
    console.log('changed by manage task');
    setAttachments(currentNote?.attachments || []);
  }, [currentNote]);

  useEffect(() => {
    if (notesContainerRef.current) {
      notesContainerRef.current.scrollTop =
        notesContainerRef.current.scrollHeight;
    }
  }, [notes]);

  useEffect(() => {
    for (const att of attachments) {
      if (!att?._id) {
        createAttachment(att);
      }
    }
  }, [attachments]);

  const createAttachment = (att) => {
    if (!att?._id && currentNote?._id) {
      AxiosConfig.create('post', `/notes/${currentNote?._id}/attachments`)
        .call({
          _id: att?._id,
          content: att?.content,
          file: {
            type: att.file?.type || att?.mimeType,
            size: att.file?.size || att?.size,
            name: att.file?.name || att?.name,
            cid: att?.cid,
            lastModifiedDate: att.file?.lastModifiedDate,
          },
          link: att?.link,
          isAtt: att?.isAtt,
        })
        .exit((res) => {
          setCurrentNote((_state) => res?.data);
          mutate();
        })
        .catch((err) => console.error(err));
    }
  };

  const deleteAttachment = (att) => {
    if (att?._id && currentNote?._id) {
      AxiosConfig.create(
        'delete',
        `/notes/${currentNote?._id}/attachments/${att?._id}`
      )
        .call()
        .exit((res) => {
          setCurrentNote((_state) => res?.data);
          mutate();
        })
        .catch((err) => console.error(err));
    }
  };

  const handleCloseTask = (redirect = false) => {
    setNoteContent('');
    console.log('changed by manage task 2');
    setAttachments([]);
    setIsLoading(false);
    if (redirect) {
      navigate(
        `/project/${task?.ticket?.project?._id}/tickets/${task?.ticket?._id}/tasks`
      );
    }
  };

  const discardDraftAndClose = (_event) => {
    if (currentNote && currentNote?.isValidated === false) {
      AxiosConfig.create('delete', `/notes/${currentNote?._id}`)
        .call()
        .exit(() => {
          setCurrentNote(null);
          handleCloseTask(true);
        })
        .catch((err) => console.error(err));
    } else {
      handleCloseTask();
    }
  };

  const handleTaskConfirm = () => {
    if (currentNote) {
      setIsLoading(true);
      AxiosConfig.create('post', `/tasks/${task?._id}/addNote`)
        .call({
          userId: profile?._id,
          noteId: currentNote?._id,
          noteContent: userMentionManager.replaceNamesWithIds(noteContent),
          isTaskEnded,
          typeId: noteTypes.find(
            (noteType) =>
              noteType?.code === (isTaskEnded ? 'TASKDONE' : 'TASKNOTE')
          )?._id,
        })
        .exit((_res) => {
          setCurrentNote(null);
          mutateTask();
          mutateTicket();
          mutateUsers(`/users/`);
          handleCloseTask(false);
          if (isTaskEnded) {
            setIsTaskEnded(false);
          }
        })
        .catch((err) => console.error(err));
    }
  };

  const handleCheckboxChange = (event) => {
    setIsTaskEnded(event.target.checked);
  };

  const handleServiceChange = async (event) => {
    setSelectedService(event.target.value);

    try {
      await AxiosConfig.create('put', `/tasks/${task?._id}`).call({
        service: event.target.value,
      });
      mutateTask();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDateChange = async (date) => {
    setScheduledDate(date);

    try {
      await AxiosConfig.create('put', `/tasks/${task?._id}`).call({
        scheduledAt: date,
      });
      mutateTask();
    } catch (err) {
      console.error(err);
    }
  };

  const getNoteBackgroundColor = (noteType, theme) => {
    return (
      theme.palette.background.note[noteType] ||
      theme.palette.background.note.DEFAULT
    );
  };

  return (
    <Paper
      sx={{
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">{task?.action?.name}</Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <Select
            labelId="service-label"
            value={selectedService}
            onChange={handleServiceChange}
          >
            {services
              .filter((service) => ['SLO', 'SCO', 'SAV'].includes(service.code))
              .map((service) => (
                <MenuItem key={service._id} value={service._id}>
                  {service.code}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {task?.scheduledAt && (
          <DatePicker
            selected={scheduledDate}
            onChange={handleDateChange}
            showTimeSelect
            dateFormat="dd/MM/yyyy HH:mm"
            placeholderText="Planifier une date"
            isClearable
          />
        )}
        <Button
          onClick={discardDraftAndClose}
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
        >
          Retour
        </Button>
      </Box>

      <Divider />

      <Box
        ref={notesContainerRef}
        sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: 250, mt: 1 }}
      >
        {notes
          ?.filter((note) => note.isValidated)
          .map((note) => {
            return (
              <Paper
                elevation={8}
                key={note?._id}
                sx={{
                  backgroundColor: getNoteBackgroundColor(
                    note?.type?.code,
                    theme
                  ),
                  p: 1,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography component="span">{`${note?.user?.firstName} ${note?.user?.lastName}`}</Typography>
                  <Typography component="span">
                    {new Date(note?.date).toLocaleString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })}
                  </Typography>
                </Box>
                <Divider />
                <TypographyWithHighlightMentions text={note?.content} />
                {note?.attachments?.length > 0 && (
                  <Box
                    sx={{ display: 'flex', gap: 1, overflowX: 'auto', mt: 1 }}
                  >
                    {note?.attachments?.map((attachment) => (
                      <DownloadableAttachment
                        key={attachment?._id}
                        attachment={attachment}
                      />
                    ))}
                  </Box>
                )}
              </Paper>
            );
          })}
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextFieldWithMentions
          noteContent={noteContent}
          setNoteContent={setNoteContent}
          handleTaskConfirm={handleTaskConfirm}
        />

        <Dropzone
          attachments={attachments}
          setAttachments={setAttachments}
          deleteAttachment={deleteAttachment}
          attachmentsSize={attachmentsSize}
          setTotalAttachmentsSize={setTotalAttachmentsSize}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isTaskEnded}
              onChange={handleCheckboxChange}
              color="primary"
            />
          }
          disabled={task?.status?.code === 'DONE'}
          label="Marquer comme terminée"
        />
        <LoadingButton
          isLoading={isLoading}
          onClick={handleTaskConfirm}
          disabled={noteContent.length === 0}
          variant={'contained'}
        >
          {isTaskEnded ? 'Terminer la tâche' : 'Ajouter note'}
        </LoadingButton>
      </Box>
    </Paper>
  );
};

export default ManageTask;
